<template>
  <div>
    <div class="video-form-container col-md-12 col-xs-12">
      <form class="video-details">
        <fieldset class="form-group form-entry col-md-8">
          <label for="video-title-input">Title
            <input required
              id="video-title-input"
              type="text"
              class="form-control"
              placeholder="Type title"
              v-model="videoTitle"
              :disabled="disabled">
            <small>Please keep the title generic and not programme specific</small>
          </label>
        </fieldset>
        <fieldset class="form-group form-entry col-md-4">
          <k-date-picker
            inputId="date-recorded-input"
            v-model="recordedDate"
            label="Date Recorded"></k-date-picker>
        </fieldset>
        <fieldset class="form-group form-entry col-md-12 video-description">
          <label for="video-description-input">Description
            <textarea id="video-description-input"
              class="form-control"
              placeholder="Enter description here..."
              rows="3"
              v-model="videoDescription"
              :disabled="disabled">
            </textarea>
          </label>
        </fieldset>
      </form>
      <word-tags v-model="videoTags" v-bind="wordTagsProps"></word-tags>
    </div>
    <div class="dropfield-row">
      <div class="dropfield-col">
        <h3>{{ videoDropfieldLabel }}</h3>
        <dropfield @file="onVideoFileChange"
                   ref="videoUploadField"
                   :customMessage="videoDropfieldMessage"
                   :immediatePost="false"
                   :disableDropfield="disabled"
                   :validateFilename="true"
                   :acceptedFilesInfo="videoAcceptedFilesInfo"
                   accept="video/mp4,video/x-m4v,video/*"
                   url=""
                   id="k-video-dropfield">
        </dropfield>
      </div>
      <div class="dropfield-col">
        <h3>{{ transcriptDropfieldLabel }}</h3>
        <dropfield @file="onTranscriptFileChange"
                   ref="transcriptUploadField"
                   :customMessage="transcriptDropfieldMessage"
                   :immediatePost="false"
                   :disableDropfield="disabled"
                   accept="text/vtt"
                   url=""
                   id="k-transcript-dropfield">
        </dropfield>
      </div>
    </div>
  </div>
</template>

<style scoped>
.form-group small {
  font-style: italic;
}

.video-form-container {
  display: flex;
  flex-direction: column;
  padding-bottom: 15px;
}

.video-form-container form {
  margin-left: -15px;
}

.video-description label,
.video-details label {
  width: 100%;
}

.dropfield-row {
  display: flex;
  gap: 15px;
  width: 100%;
  padding: 15px;
  padding-right: 30px;
}

.dropfield-col {
  flex: 1;
  min-width: 0;
}

#k-video-dropfield,
#k-transcript-dropfield,
.video-details {
  padding-top: 15px;
}

textarea {
  max-width: 100%;
  resize: none;
}

.form-entry :deep(.k-date-picker label) {
  margin-bottom: 0;
}
</style>

<script>
import KDatePicker from '@base-components/k-date-picker.vue';
import Dropfield from '@base-components/dropfield.vue';
import { FILENAME_HELPER_TEXT } from '@validators';
import WordTags from '../../../components/word-tags.vue';

export default {
  components: {
    'k-date-picker': KDatePicker,
    dropfield: Dropfield,
    'word-tags': WordTags,
  },

  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    tagOptions: {
      type: Array,
      default: undefined,
    },
    videoDropfieldLabel: {
      type: String,
      default: 'Upload Video',
    },
    transcriptDropfieldLabel: {
      type: String,
      default: 'Upload Transcript',
    },
    videoFileName: {
      type: String,
      default: null,
    },
    transcriptFileName: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      videoTitle: this.modelValue.title || '',
      recordedDate: this.modelValue.dateRecorded || null,
      videoDescription: this.modelValue.description || '',
      videoTags: this.modelValue.tags || [],
      defaultDropfieldMessage: `<i class="fas fa-cloud-upload-alt" aria-hidden="true"></i>
      Click or drag file here.`,
      videoAcceptedFilesInfo: `<p>${FILENAME_HELPER_TEXT}</p>`,
    };
  },

  mounted() {
    this.$emit('isValid', this.isValid);
  },

  watch: {
    videoTitle(newValue) {
      this.$emit('update:modelValue', { ...this.modelValue, title: newValue });
      this.$emit('isValid', this.isValid);
    },
    recordedDate(newValue) {
      this.$emit('update:modelValue', { ...this.modelValue, dateRecorded: newValue });
      this.$emit('isValid', this.isValid);
    },
    videoDescription(newValue) {
      this.$emit('update:modelValue', { ...this.modelValue, description: newValue });
      this.$emit('isValid', this.isValid);
    },
    videoTags: {
      handler(newValue) {
        this.$emit('update:modelValue', { ...this.modelValue, tags: newValue });
        this.$emit('isValid', this.isValid);
      },
      deep: true,
    },
  },

  computed: {
    isValid() {
      return this.videoTitle.trim().length > 0;
    },
    videoDropfieldMessage() {
      if (this.videoFileName) {
        return `<i class="fas fa-file-alt" aria-hidden="true"></i> ${this.videoFileName}<br><small>Click or drag to replace</small>`;
      }
      return this.defaultDropfieldMessage;
    },
    transcriptDropfieldMessage() {
      if (this.transcriptFileName) {
        return `<i class="fas fa-file-alt" aria-hidden="true"></i> ${this.transcriptFileName}<br><small>Click or drag to replace</small>`;
      }
      return this.defaultDropfieldMessage;
    },
    wordTagsProps() {
      if (this.tagOptions) {
        return {
          options: this.tagOptions,
          dropdownPlaceholder: 'Modules',
          dropdownSearchPlaceholder: 'Search by module name or standardised code',
        };
      }
      return {
        textInputPlaceholder: 'Add a tag',
      };
    },
  },

  methods: {
    onVideoFileChange(file) {
      this.$emit('video-file-change', file);
    },
    onTranscriptFileChange(file) {
      this.$emit('transcript-file-change', file);
    },
    reset() {
      this.videoTitle = '';
      this.recordedDate = null;
      this.videoDescription = '';
      this.videoTags = [];
      if (this.$refs.videoUploadField) {
        this.$refs.videoUploadField.reset();
      }
      if (this.$refs.transcriptUploadField) {
        this.$refs.transcriptUploadField.reset();
      }
    },
  },
};
</script>
